import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ROLE_LABELS, ROLES } from '../contexts/AuthContext';
import NotificationCenter from '../components/NotificationCenter';
import LicenseWarningBanner from '../components/LicenseWarningBanner';
import { searchService, moduleService, sitesService } from '../services/api';
import {
  LayoutDashboard,
  Users,
  Building2,
  Plug,
  Clock,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Zap,
  Server,
  Shield,
  Database,
  Calendar,
  Award,
  Activity,
  Bell,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  Search,
  AlertTriangle,
  BookOpen,
  DollarSign,
  Receipt,
  Map,
  MapPin,
  UserCog,
  Wrench,
  Newspaper,
  PenSquare,
  MessageSquare,
  ClipboardList,
  Eye,
  EyeOff,
  ChevronDown,
  UserCheck,
  Gauge
} from 'lucide-react';

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return { theme: 'light', toggleTheme: () => {} };
  }
  return context;
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('hims-theme') || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('hims-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Rol Simülasyonu Dropdown Bileşeni
const RoleSimulationDropdown = ({ navigate }) => {
  const { 
    isRealSuperAdmin, 
    simulatedRole, 
    startRoleSimulation, 
    stopRoleSimulation, 
    isSimulationActive 
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showSiteSelect, setShowSiteSelect] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const dropdownRef = useRef(null);

  // Dropdown dışına tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowSiteSelect(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Site listesini yükle
  const loadSites = async () => {
    setLoadingSites(true);
    try {
      const data = await sitesService.list();
      // API response'u parse et - array veya obje olabilir
      const sitesArray = Array.isArray(data) ? data : (data?.sites || data?.items || []);
      setSites(sitesArray);
    } catch (err) {
      console.error('Sites yüklenemedi:', err);
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  };
  
  // Site seçimi gerektiren roller - Artık otomatik seçiliyor
  const siteRequiredRoles = [];
  
  const handleRoleSelect = async (role) => {
    if (siteRequiredRoles.includes(role)) {
      // Site seçimi gerekiyor
      setPendingRole(role);
      await loadSites();
      setShowSiteSelect(true);
    } else {
      // Direkt simülasyon başlat - await ile bekle ki siteId set edilsin
      await startRoleSimulation(role);
      setIsOpen(false);
      handleNavigation(role);
    }
  };
  
  const handleSiteSelect = (siteId) => {
    startRoleSimulation(pendingRole, siteId);
    setShowSiteSelect(false);
    setIsOpen(false);
    handleNavigation(pendingRole);
    setPendingRole(null);
  };
  
  const handleNavigation = (role) => {
    // Usta/Teknisyen seçilince /technician sayfasına yönlendir
    if (role === 'technician' && navigate) {
      setTimeout(() => navigate('/technician'), 100);
    }
    // Site Sakini seçilince /resident/dashboard sayfasına yönlendir
    if (role === 'site_sakini' && navigate) {
      setTimeout(() => navigate('/resident/dashboard'), 100);
    }
    // İçerik Yöneticisi seçilince /news sayfasına yönlendir
    if (role === 'icerik_yoneticisi' && navigate) {
      setTimeout(() => navigate('/news'), 100);
    }
    // Yardımcı seçilince /yardimci/dashboard sayfasına yönlendir
    if (role === 'yardimci' && navigate) {
      setTimeout(() => navigate('/yardimci/dashboard'), 100);
    }
  };

  // Sadece gerçek süper admin için göster
  if (!isRealSuperAdmin()) return null;

  // Simüle edilebilecek roller - ROLE_LABELS'dan dinamik olarak oluştur
  // Super Admin ve duplike roller hariç
  const excludedRoles = ['super_admin', 'user', 'sakini', 'bireysel_yonetici', 'site_manager'];
  const roleIcons = {
    admin: '👔',
    hesap_yoneticisi: '📊',
    yardimci: '🤝',
    icerik_yoneticisi: '📝',
    site_sakini: '🏠',
    sofor: '🚗',
    calisan: '👷',
    ziyaretci: '👋',
    bireysel_hesap: '👤',
    technician: '🔧'
  };
  
  const simulationRoles = Object.entries(ROLE_LABELS)
    .filter(([role]) => !excludedRoles.includes(role))
    .map(([role, label]) => ({
      role,
      label: `${label} görünümü`,
      icon: roleIcons[role] || '👤'
    }));
  
  // Teknisyen rolünü ekle (ROLE_LABELS'da yok)
  if (!simulationRoles.find(r => r.role === 'technician')) {
    simulationRoles.push({
      role: 'technician',
      label: 'Usta/Teknisyen görünümü',
      icon: '🔧'
    });
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
          ${isSimulationActive() 
            ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' 
            : 'bg-primary/10 text-primary hover:bg-primary/20'
          }
        `}
        data-testid="role-simulation-btn"
      >
        <Eye className="w-4 h-4" />
        <span className="hidden md:inline">
          {isSimulationActive() ? 'Simülasyon Aktif' : 'Rol Simülasyonu'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 rounded-xl bg-card border border-border shadow-xl z-50 overflow-hidden">
          <div className="p-3 border-b border-border bg-muted/50">
            <p className="text-xs font-medium text-muted-foreground">
              {showSiteSelect ? 'Simüle edilecek siteyi seçin' : 'Farklı rollerin göreceği ekranı simüle edin'}
            </p>
          </div>
          
          {showSiteSelect ? (
            // Site seçim listesi
            <div className="p-2 max-h-80 overflow-y-auto">
              {loadingSites ? (
                <div className="p-4 text-center text-muted-foreground">
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2"></span>
                  Siteler yükleniyor...
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowSiteSelect(false)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-accent rounded-lg mb-2"
                  >
                    ← Geri
                  </button>
                  {sites.map((site) => (
                    <button
                      key={site.id}
                      onClick={() => handleSiteSelect(site.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left hover:bg-accent"
                    >
                      <span className="text-lg">🏢</span>
                      <span>{site.name}</span>
                    </button>
                  ))}
                </>
              )}
            </div>
          ) : (
            // Rol seçim listesi
            <div className="p-2 max-h-80 overflow-y-auto">
              {simulationRoles.map((item) => (
                <button
                  key={item.role}
                  onClick={() => handleRoleSelect(item.role)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left
                    ${simulatedRole === item.role 
                      ? 'bg-primary/20 text-primary' 
                      : 'hover:bg-accent text-foreground'
                    }
                  `}
                  data-testid={`simulate-${item.role}`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span>{item.label}</span>
                  {siteRequiredRoles.includes(item.role) && (
                    <span className="text-xs text-muted-foreground ml-auto">Site seç →</span>
                  )}
                  {simulatedRole === item.role && (
                    <UserCheck className="w-4 h-4 ml-auto text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}

          {isSimulationActive() && (
            <div className="p-2 border-t border-border">
              <button
                onClick={() => {
                  stopRoleSimulation();
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
                data-testid="stop-simulation-btn"
              >
                <EyeOff className="w-4 h-4" />
                Simülasyonu Bitir
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Simülasyon Modu Banner
const SimulationBanner = () => {
  const { isSimulationActive, simulatedRole, stopRoleSimulation } = useAuth();

  if (!isSimulationActive()) return null;

  const getRoleLabel = () => {
    const labels = {
      [ROLES.ADMIN]: 'Admin',
      [ROLES.HESAP_YONETICISI]: 'Hesap Yöneticisi',
      [ROLES.YARDIMCI]: 'Yardımcı',
      [ROLES.SITE_SAKINI]: 'Site Sakini',
      [ROLES.CALISAN]: 'Çalışan/Şoför',
      [ROLES.ICERIK_YONETICISI]: 'İçerik Yöneticisi',
      'icerik_yoneticisi': 'İçerik Yöneticisi',
      'ziyaretci': 'Ziyaretçi',
      'bireysel_hesap': 'Bireysel Hesap',
      'bireysel_yonetici': 'Bireysel Hesap',
      'technician': 'Usta/Teknisyen',
      'calisan': 'Çalışan/Şoför',
      'sofor': 'Şoför'
    };
    return labels[simulatedRole] || ROLE_LABELS[simulatedRole] || simulatedRole;
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white py-2 px-4 flex items-center justify-center gap-4 shadow-lg">
      <div className="flex items-center gap-2">
        <Eye className="w-5 h-5 animate-pulse" />
        <span className="font-bold">SİMÜLASYON MODU:</span>
        <span className="font-medium">{getRoleLabel()}</span>
      </div>
      <button
        onClick={stopRoleSimulation}
        className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
        data-testid="stop-simulation-banner-btn"
      >
        <EyeOff className="w-4 h-4" />
        Simülasyonu Bitir
      </button>
    </div>
  );
};

// Logo Component
const Logo = ({ collapsed }) => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
        <Zap className="h-6 w-6 text-white" strokeWidth={2.5} />
      </div>
      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-900" />
    </div>
    {!collapsed && (
      <div className="overflow-hidden">
        <h1 className="font-bold text-xl tracking-tight">HIMS</h1>
        <p className="text-xs text-muted-foreground truncate">Şarj İstasyonu Yönetimi</p>
      </div>
    )}
  </div>
);

// Navigation Item Component
const NavItem = ({ item, isActive, collapsed, onClick }) => {
  const Icon = item.icon;
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick();
  };
  
  return (
    <Link
      to={item.path}
      onClick={handleClick}
      data-testid={`nav-${item.path.replace('/', '')}`}
      data-path={item.path}
      className={`
        relative block w-full
        group flex items-center gap-3 px-3 py-2 rounded-lg
        ${isActive 
          ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20' 
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
        }
        ${collapsed ? 'justify-center' : ''}
        transition-all duration-200
      `}
      style={{ isolation: 'isolate' }}
    >
      <Icon className="h-4 w-4 flex-shrink-0 pointer-events-none" strokeWidth={1.5} />
      {!collapsed && (
        <>
          <span className="font-medium text-sm pointer-events-none">{item.name}</span>
          {item.badge && (
            <span className="ml-auto px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-emerald-500 text-white pointer-events-none">
              {item.badge}
            </span>
          )}
        </>
      )}
      {collapsed && item.badge && (
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full pointer-events-none" />
      )}
    </Link>
  );
};

// Navigation Group Component
const NavGroup = ({ title, icon, items, collapsed, currentPath, onItemClick }) => {
  const [isOpen, setIsOpen] = useState(true);
  const hasActiveItem = items.some(item => currentPath === item.path);
  
  if (items.length === 0) return null;
  
  return (
    <div className="mb-2">
      {!collapsed && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider
            ${hasActiveItem ? 'text-primary' : 'text-muted-foreground'}
            hover:text-foreground transition-colors
          `}
        >
          <span>{icon}</span>
          <span className="flex-1 text-left">{title}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
        </button>
      )}
      {(isOpen || collapsed) && (
        <div className={`${collapsed ? '' : 'pl-2'} space-y-0.5`}>
          {items.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              isActive={currentPath === item.path}
              collapsed={collapsed}
              onClick={onItemClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Search Bar Component with Results
const SearchBar = ({ navigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    try {
      const data = await searchService.search(searchQuery);
      setResults(data?.results || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    setQuery('');
    navigate(result.path);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'user': return <Users className="h-4 w-4 text-blue-500" />;
      case 'site': return <Building2 className="h-4 w-4 text-emerald-500" />;
      case 'station': return <Plug className="h-4 w-4 text-violet-500" />;
      case 'rfid': return <CreditCard className="h-4 w-4 text-amber-500" />;
      default: return <Search className="h-4 w-4 text-slate-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'user': return 'Kullanıcı';
      case 'site': return 'Site';
      case 'station': return 'İstasyon';
      case 'rfid': return 'RFID Kart';
      default: return type;
    }
  };

  return (
    <div className="relative max-w-md flex-1" ref={searchRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => results.length > 0 && setShowResults(true)}
        placeholder="Kullanıcı, site, istasyon ara..."
        className="w-full h-10 pl-10 pr-4 rounded-xl bg-accent/50 border-0 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
        data-testid="global-search-input"
      />
      
      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              <div className="animate-spin inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
              <span className="ml-2">Aranıyor...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <p>"{query}" için sonuç bulunamadı</p>
            </div>
          ) : (
            <div className="py-2">
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}-${index}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-accent/50 transition-colors text-left"
                  data-testid={`search-result-${index}`}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                    {getTypeIcon(result.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{result.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                  </div>
                  <span className="flex-shrink-0 text-xs text-muted-foreground bg-accent px-2 py-1 rounded-lg">
                    {getTypeLabel(result.type)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// User Profile Card
const UserCard = ({ user, collapsed, onLogout }) => {
  const { isSimulationActive, simulatedRole, getEffectiveRole } = useAuth();
  
  // Simülasyon modunda rol adını göster
  const displayName = isSimulationActive() 
    ? getRoleDisplayName(simulatedRole)
    : (user?.full_name || user?.email);
  
  const displayRole = isSimulationActive()
    ? getRoleDisplayName(simulatedRole)
    : (ROLE_LABELS[user?.role] || user?.role);
  
  const initials = isSimulationActive()
    ? simulatedRole?.substring(0, 2)?.toUpperCase() || 'SM'
    : user?.full_name
      ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
      : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className={`p-3 ${collapsed ? 'flex flex-col items-center' : ''}`}>
      {!collapsed && (
        <div className={`flex items-center gap-3 p-3 rounded-xl mb-3 ${isSimulationActive() ? 'bg-red-500/20 border border-red-500/30' : 'bg-accent/30'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-lg ${isSimulationActive() ? 'bg-gradient-to-br from-red-500 to-orange-600' : 'bg-gradient-to-br from-violet-500 to-purple-600'}`}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{displayName}</p>
            <p className={`text-xs ${isSimulationActive() ? 'text-red-400' : 'text-muted-foreground'}`}>
              {displayRole}
              {isSimulationActive() && ' (Simülasyon)'}
            </p>
          </div>
        </div>
      )}
      <button
        onClick={onLogout}
        className={`
          flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm font-medium
          text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-all duration-200
          ${collapsed ? 'justify-center' : ''}
        `}
      >
        <LogOut className="h-5 w-5" strokeWidth={1.5} />
        {!collapsed && <span>Çıkış Yap</span>}
      </button>
    </div>
  );
};

// Rol görünen adı
const getRoleDisplayName = (role) => {
  const roleNames = {
    [ROLES.ADMIN]: 'Admin',
    [ROLES.HESAP_YONETICISI]: 'Hesap Yöneticisi',
    [ROLES.SITE_MANAGER]: 'Site Yöneticisi',
    [ROLES.YARDIMCI]: 'Yardımcı',
    [ROLES.SITE_SAKINI]: 'Site Sakini',
    [ROLES.CALISAN]: 'Çalışan/Şoför',
    [ROLES.ICERIK_YONETICISI]: 'İçerik Yöneticisi',
    [ROLES.ZIYARETCI]: 'Ziyaretçi',
    [ROLES.BIREYSEL_HESAP]: 'Bireysel Hesap',
    [ROLES.BIREYSEL_YONETICI]: 'Bireysel Hesap',
    'technician': 'Usta/Teknisyen',
    'calisan': 'Çalışan/Şoför',
    'icerik_yoneticisi': 'İçerik Yöneticisi',
    'ziyaretci': 'Ziyaretçi',
    'bireysel_hesap': 'Bireysel Hesap',
    'bireysel_yonetici': 'Bireysel Hesap',
    'sofor': 'Şoför'
  };
  return roleNames[role] || ROLE_LABELS[role] || role;
};

// Theme Toggle Button
const ThemeToggle = ({ collapsed }) => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={`
        flex items-center gap-2 w-full px-3 py-2.5 rounded-xl text-sm font-medium
        text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all duration-200
        ${collapsed ? 'justify-center' : ''}
      `}
    >
      {theme === 'dark' ? (
        <>
          <Sun className="h-5 w-5" strokeWidth={1.5} />
          {!collapsed && <span>Açık Tema</span>}
        </>
      ) : (
        <>
          <Moon className="h-5 w-5" strokeWidth={1.5} />
          {!collapsed && <span>Koyu Tema</span>}
        </>
      )}
    </button>
  );
};

// Main Dashboard Layout
const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin, isSuperAdmin, isSuperOrAdmin, isHesapYoneticisi, isBireyselYonetici, isSiteSakini, isYardimci, isCalisan, canAccessAuditLogs, canAccessBackups, canAccessScheduler, canAccessLicenses, canAccessEnergy, canAccessOCPP, canAccessModuleManagement, canManageContent, isIcerikYoneticisi, isSimulationActive, getEffectiveRole, simulatedRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    }
    return false;
  });
  const sidebarRef = useRef(null);
  const mainRef = useRef(null);
  
  // Simülasyon aktif mi?
  const simulationActive = isSimulationActive();
  
  // Module config state - news_center ve board modül durumu
  const [moduleConfig, setModuleConfig] = useState({ news_center: true, board: true });
  
  // Modül config'ini yükle
  useEffect(() => {
    const loadModuleConfig = async () => {
      try {
        // Kullanıcının site'ını bul
        if (user?.site_id) {
          const config = await moduleService.get(user.site_id);
          setModuleConfig(prev => ({
            ...prev,
            news_center: config.news_center !== false,
            board: config.board !== false
          }));
        } else if (isSuperOrAdmin()) {
          // Admin için ilk site'ın config'ini al
          const sites = await sitesService.list();
          if (sites && sites.length > 0) {
            const config = await moduleService.get(sites[0].id);
            setModuleConfig(prev => ({
              ...prev,
              news_center: config.news_center !== false,
              board: config.board !== false
            }));
          }
        }
      } catch (error) {
        console.error('Module config load error:', error);
        // Hata durumunda varsayılan olarak göster
        setModuleConfig({ news_center: true, board: true });
      }
    };
    
    if (user) {
      loadModuleConfig();
    }
  }, [user, isSuperOrAdmin]);
  
  // Scroll pozisyonunu kaydet (her scroll'da)
  const saveScrollPosition = useCallback(() => {
    if (sidebarRef.current) {
      sessionStorage.setItem('sidebar-scroll', sidebarRef.current.scrollTop.toString());
    }
  }, []);

  // Collapsed state değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed.toString());
  }, [collapsed]);

  // Scroll pozisyonunu geri yükle (route değişiminde)
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('sidebar-scroll');
    if (savedScroll && sidebarRef.current) {
      const scrollValue = parseInt(savedScroll, 10);
      // Birden fazla kez dene (React render cycle'ları için)
      sidebarRef.current.scrollTop = scrollValue;
      requestAnimationFrame(() => {
        if (sidebarRef.current) {
          sidebarRef.current.scrollTop = scrollValue;
        }
      });
      setTimeout(() => {
        if (sidebarRef.current) {
          sidebarRef.current.scrollTop = scrollValue;
        }
      }, 50);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Menu items with role-based visibility - KATEGORİLİ YAPI
  // SÜPER ADMIN: Tüm kategoriler
  // HESAP YÖNETİCİSİ: Dashboard, Şarj, Yönetim, Operasyon, Mali kategorileri
  // SİTE SAKİNİ: Ana Sayfa, Şarj, Geçmiş, Bildirimler
  // USTA/TEKNİSYEN: İş Emirleri, Arıza Bildir, Profil
  
  // Efektif rol kontrolü
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const isEffectiveTechnician = effectiveRole === 'technician';
  const isEffectiveCalisan = effectiveRole === ROLES.CALISAN || effectiveRole === 'calisan';
  const isEffectiveYardimci = effectiveRole === ROLES.YARDIMCI;
  const isEffectiveSiteSakini = effectiveRole === ROLES.SITE_SAKINI || effectiveRole === ROLES.USER;
  const isEffectiveHesapYoneticisi = effectiveRole === ROLES.HESAP_YONETICISI;
  const isEffectiveZiyaretci = effectiveRole === ROLES.ZIYARETCI || effectiveRole === 'ziyaretci';
  const isEffectiveBireyselHesap = effectiveRole === ROLES.BIREYSEL_HESAP || effectiveRole === ROLES.BIREYSEL_YONETICI || effectiveRole === 'bireysel_hesap';
  const isEffectiveIcerikYoneticisi = effectiveRole === ROLES.ICERIK_YONETICISI || effectiveRole === 'icerik_yoneticisi';

  // Kategori bazlı menü yapısı
  const menuCategories = [
    // GENEL BAKIŞ
    {
      title: 'Genel Bakış',
      icon: '📊',
      items: [
        { name: 'Patron Paneli', path: '/kokpit', icon: Gauge, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi || isBireyselYonetici() || isEffectiveBireyselHesap) && !isYardimci() && !isCalisan() && !isEffectiveTechnician && !isEffectiveCalisan && !isIcerikYoneticisi() && !isEffectiveIcerikYoneticisi && !isEffectiveYardimci && !isEffectiveSiteSakini },
        { name: 'Dashboard', path: '/yardimci/dashboard', icon: LayoutDashboard, show: isYardimci() || isEffectiveYardimci },
        { name: 'Dashboard', path: '/technician', icon: LayoutDashboard, show: isEffectiveTechnician },
      ].filter(item => item.show)
    },
    // ŞARJ YÖNETİMİ
    {
      title: 'Şarj Yönetimi',
      icon: '⚡',
      items: [
        { name: 'OCPP İzleme', path: '/ocpp', icon: Server, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Cihaz Yöneticisi', path: '/device-manager', icon: Server, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'İstasyonlar', path: '/stations', icon: Plug, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Aktif Oturumlar', path: '/sessions', icon: Clock, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi || isBireyselYonetici() || isEffectiveBireyselHesap) && !isYardimci() && !isCalisan() && !isEffectiveTechnician && !isEffectiveCalisan && !isIcerikYoneticisi() && !isEffectiveZiyaretci && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveIcerikYoneticisi },
      ].filter(item => item.show)
    },
    // KULLANICI YÖNETİMİ
    {
      title: 'Kullanıcı Yönetimi',
      icon: '👥',
      items: [
        { name: 'Kullanıcılar', path: '/users', icon: Users, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'RFID Kartlar', path: '/rfid', icon: CreditCard, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isYardimci() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Hesaplar', path: '/sites', icon: Building2, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Lokasyonlar', path: '/locations', icon: MapPin, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi || isYardimci() || isEffectiveYardimci) && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Harita', path: '/map', icon: Map, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
      ].filter(item => item.show)
    },
    // OPERASYON
    {
      title: 'Operasyon',
      icon: '🔧',
      items: [
        { name: 'Arıza Yönetimi', path: '/faults', icon: AlertTriangle, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi || isYardimci() || isEffectiveYardimci) && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'İş Emirleri', path: '/work-orders', icon: ClipboardList, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Usta Yönetimi', path: '/technicians', icon: Wrench, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
      ].filter(item => item.show)
    },
    // MALİ
    {
      title: 'Mali',
      icon: '💰',
      items: [
        { name: 'Tarifeler', path: '/tariffs', icon: DollarSign, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi || isBireyselYonetici() || isEffectiveBireyselHesap) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveIcerikYoneticisi },
        { name: 'Enerji Raporu', path: '/energy', icon: Activity, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Raporlar', path: '/reports', icon: FileText, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi || isBireyselYonetici() || isEffectiveBireyselHesap) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveIcerikYoneticisi },
        { name: 'Lisanslar', path: '/licenses', icon: Award, show: (isSuperOrAdmin() || isBireyselYonetici() || isEffectiveBireyselHesap || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveIcerikYoneticisi },
        { name: 'Lisans Dağıtımı', path: '/license-distribution', icon: CreditCard, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Ödeme Onayları', path: '/payment-approvals', icon: CreditCard, show: (isSuperAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Fatura Analizi', path: '/invoice-analysis', icon: Receipt, show: (isBireyselYonetici() || isEffectiveBireyselHesap) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveHesapYoneticisi && !isEffectiveIcerikYoneticisi },
      ].filter(item => item.show)
    },
    // İÇERİK
    {
      title: 'İçerik',
      icon: '📰',
      items: [
        { name: 'Haber Merkezi', path: '/news', icon: Newspaper, show: moduleConfig.news_center === true && !isEffectiveTechnician },
        { name: 'Pano', path: '/board', icon: MessageSquare, show: moduleConfig.board !== false && (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isCalisan() && !isYardimci() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveSiteSakini && !isEffectiveIcerikYoneticisi },
        { name: 'İçerik Yönetimi', path: '/content-management', icon: PenSquare, show: canManageContent() && moduleConfig.news_center === true && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveHesapYoneticisi && !isEffectiveZiyaretci && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveBireyselHesap },
      ].filter(item => item.show)
    },
    // SİSTEM (Sadece Super Admin)
    {
      title: 'Sistem',
      icon: '⚙️',
      items: [
        { name: 'Yönetim Paneli', path: '/management', icon: UserCog, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Modül Yönetimi', path: '/modules', icon: Settings, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Denetim Kayıtları', path: '/audit-logs', icon: Shield, show: isSuperOrAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Yedeklemeler', path: '/backups', icon: Database, show: isSuperAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Zamanlayıcı', path: '/scheduler', icon: Calendar, show: isSuperAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Bildirim Ayarları', path: '/notification-settings', icon: Bell, show: true },
        { name: 'Dokümantasyon', path: '/documentation', icon: BookOpen, show: (isSuperOrAdmin() || isHesapYoneticisi() || isEffectiveHesapYoneticisi) && !isYardimci() && !isCalisan() && !isIcerikYoneticisi() && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveYardimci && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
        { name: 'Sistem Ayarları', path: '/settings', icon: Settings, show: isSuperAdmin() && !isEffectiveHesapYoneticisi && !isEffectiveYardimci && !isEffectiveTechnician && !isEffectiveCalisan && !isEffectiveSiteSakini && !isEffectiveZiyaretci && !isEffectiveBireyselHesap && !isEffectiveIcerikYoneticisi },
      ].filter(item => item.show)
    },
  ];

  // Boş kategorileri filtrele
  const visibleCategories = menuCategories.filter(cat => cat.items.length > 0);

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background">
        {/* License Warning Banner */}
        <LicenseWarningBanner />

        {/* Mobile Header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 px-4 flex items-center justify-between bg-background/80 backdrop-blur-xl border-b border-border">
          <Logo collapsed={false} />
          <div className="flex items-center gap-2">
            {/* Mobile için küçük simülasyon butonu */}
            <RoleSimulationDropdown navigate={navigate} />
            <NotificationCenter />
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-xl hover:bg-accent transition-colors"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </header>

        {/* Mobile Overlay */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed top-0 left-0 z-50 h-full bg-card border-r border-border
            transform transition-all duration-300 ease-in-out
            lg:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            ${collapsed ? 'w-20' : 'w-72'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* Logo Section */}
            <div className={`p-4 border-b border-border ${collapsed ? 'flex justify-center' : ''}`}>
              <Logo collapsed={collapsed} />
            </div>

            {/* Collapse Toggle (Desktop only) */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-primary text-primary-foreground items-center justify-center shadow-lg hover:scale-110 transition-transform"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>

            {/* Navigation */}
            <nav 
              ref={sidebarRef}
              onScroll={saveScrollPosition}
              className="flex-1 overflow-y-auto p-3 space-y-1 no-scrollbar" 
              style={{ overscrollBehavior: 'contain' }}
            >
              {visibleCategories.map((category) => (
                <NavGroup
                  key={category.title}
                  title={category.title}
                  icon={category.icon}
                  items={category.items}
                  collapsed={collapsed}
                  currentPath={location.pathname}
                  onItemClick={() => setSidebarOpen(false)}
                />
              ))}
            </nav>

            {/* Bottom Section */}
            <div className="border-t border-border">
              <div className="p-3">
                <ThemeToggle collapsed={collapsed} />
              </div>
              <UserCard user={user} collapsed={collapsed} onLogout={handleLogout} />
            </div>
          </div>
        </aside>

        {/* Simülasyon Modu Banner */}
        <SimulationBanner />

        {/* Desktop Header */}
        <header 
          className={`
            hidden lg:flex fixed top-0 right-0 z-40 h-16 items-center justify-between px-6
            bg-background/80 backdrop-blur-xl border-b border-border
            ${collapsed ? 'left-20' : 'left-72'}
          `}
          style={{ transition: 'left 0.3s ease-in-out' }}
        >
          {/* Left Section - Empty for balance */}
          <div></div>
          
          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Rol Simülasyonu Butonu - Sadece Süper Admin için */}
            <RoleSimulationDropdown navigate={navigate} />
            <NotificationCenter />
          </div>
        </header>

        {/* Main Content */}
        <main
          ref={mainRef}
          id="main-content"
          className={`
            min-h-screen overflow-x-hidden overflow-y-auto
            ${simulationActive ? 'pt-28 lg:pt-28' : 'pt-16 lg:pt-16'}
            ${collapsed ? 'lg:ml-20' : 'lg:ml-72'}
          `}
          style={{ transition: 'margin-left 0.3s ease-in-out' }}
        >
          <div className="p-4 lg:p-6 overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>
    </ThemeProvider>
  );
};

export default DashboardLayout;
